/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React from 'react';
import PropTypes from 'prop-types';
import Screen from '../Screen';
import Entitytypeview from '../../Entitytypeview/Entitytypeview';
import Fab from '../../Fab/Fab';
import Button from '../../Button/Button';
import Dialog from '../../Dialog/Dialog';
import Service from '../../Util/Service';
import $ from '../../Util/Util';
import AddIcon from './baseline_add_white_24dp.png';
import './Entitytypes.css';

/**
 * Represents the entity type maintenance screen.
 */

export default class Entitytypes extends Screen {

    /**
     * Properties.
     */

    static propTypes = {
        onEntitytypeSelected:              PropTypes.func.isRequired
    }

    /**
     * Default state.
     */

    state = {
        entitytypes:     [],
        entitytype:      null
    }

    /**
     * Loads the entity types.
     */

    _refresh = () => {

        // Showing the progress indicator

        this.props.onLoading(true);

        // Loading the entity types

        Service.loadEntitytype().then((entitytypes) => {

            // Storing the entity types

            this.setState({ entitytypes: entitytypes });

            // Hiding the progress indicator

            this.props.onLoading(false);
        }).catch((error) => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /**
     * Events that fires when the user selects an entity type from the list.
     * 
     * @param   {Object}    entitytype      selected entity type
     */

    _entitytypeSelected = (entitytype) => {

        // Do nothing if there is no ID of the selected entity type

        if (this.state.entitytype && !this.state.entitytype.id ) {
            return;
        }

        // Storing the selected entity type

        if (this.props.context.environment.browser.info.mobile) {
            this.props.onEntitytypeSelected(entitytype);
        } else {
            this.setState({ entitytype: (this.state.entitytype && this.state.entitytype.id === entitytype.id) ? null : entitytype });
        }
    }

    /**
     * Adds a new entity type.
     */

    _addNewEntitytype = () => {

        // Create the entity type

        let entitytype = { 
            name:               this.props.context.locale.entitytype.newentitytype,
            entitycreateaccess: [],
            attributes:         []
        };

        // Storing the entity type

        if (this.props.context.environment.browser.info.mobile) {
            this.props.onEntitytypeSelected(entitytype);
        } else {
            this.setState({ entitytype: entitytype });
        }
    }

    /**
     * Event that fires when the user closes the entity type maintenance.
     * 
     * @param   {Boolean}   refresh     refreshing the entity types
     */ 

    _onCloseMaintenance = (refresh) => {

        // Clearing the entity types

        this.setState( { entitytype: null });

        // Refreshing the entity types

        if (refresh) {
            this._refresh();
        }
    }

    /**
     * Event that fires when the user wants to delete the selected entity type.
     */

    _onDeleteEntitytype = () => {
        this.deleteConfirmDialog.open();
    }

    /**
     * Event that fires when the user wants to cancel the deletion of the
     * selected entity type.
     */

    _cancelDelete = () => {
        this.deleteConfirmDialog.close();
    }

    /**
     * Deletes the selected entity type.
     */

    _delete = () => {

        // Closing confirm dialog

        this.deleteConfirmDialog.close();

        // Showing progress indicator

        this.props.onLoading(true);

        // Deleting the entity type

        Service.deleteEntitytype(this.state.entitytype.id).then(() => {

            // Hiding progress indicator

            this.props.onLoading(false);

            // Closing maintenance mode

            this.setState({ entitytype: null }, () => {

                // Showing confirm

                $.fireEvent('snackbar', { message:  this.props.context.locale.entitytype.deletedmessage });

                // Refreshing

                this._refresh();
            });
        }).catch((error) => {

            // Hiding progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /** 
     * Event that fires when the component mounted.
     */

    componentDidMount() {

        // Setting actionbar title

        this.props.onSetActionbarTitle(this.props.context.locale.entitytype.title.maintitle);

        // Refreshing the entity types

        this._refresh();
    }

    /** 
     * Entity type maintenance screen renderer.
     */

    render() {
        return (
            <div className="entitytypes">

                {/* Entity types container */}

                <div className="entitytypes-container horizontal">

                    {/* List of entity types */}

                    <div className="entitytypes-list vertical" style={{ height: (this.props.context.environment.screen.usefullheight - 32) + 'px' }}>
                        { this.state.entitytypes.map((item, index) =>
                            <div key={index} className={`entitytypes-entitytype ${(this.state.entitytype && this.state.entitytype.id === item.id) ? 'entitytypes-entitytype-selected' : 'entitytypes-entitytype-unselected'}`} onClick={this._entitytypeSelected.bind(this, item)}>{item.name}</div>
                        )}

                        {/* Add new entity type */}

                        { !this.state.entitytype && <div className="entitytypes-fab">
                            <Fab icon={AddIcon} tooltip={this.props.context.locale.entitytype.newentitytypetooltip} onTouch={this._addNewEntitytype} />
                        </div> }
                    </div>

                    {/* Maintenance */}

                    { !this.props.context.environment.browser.info.mobile && this.state.entitytype && <div className="entitytypes-maintenance">
                        <Entitytypeview context={this.props.context} entitytype={this.state.entitytype} onLoading={this.props.onLoading} onClose={this._onCloseMaintenance} onDelete={this._onDeleteEntitytype} />
                    </div> }
                </div>

                {/* Delete confirm dialog */}

                <Dialog ref={c => {this.deleteConfirmDialog = c;}} width={360} height={104} closetooltip={this.props.context.locale.entitytype.deletedialog.close}>

                    {/* Message */}

                    <div className="entitytypes-deletedialog-title">{this.props.context.locale.entitytype.deletedialog.message}</div>
                    <div className="entitytypes-deletedialog-entitytypename">{this.state.entitytype ? this.state.entitytype.name : ''}</div>

                    {/* Buttons */}

                    <div className="horizontal">

                        {/* Spacer */}

                        <div className="flex"></div>

                        {/* Delete */}

                        <Button label={this.props.context.locale.entitytype.deletedialog.buttons.yes.label} tooltip={this.props.context.locale.entitytype.deletedialog.buttons.yes.tooltip} width={60} onTouch={this._delete} />

                        {/* Cancel */}

                        <Button label={this.props.context.locale.entitytype.deletedialog.buttons.cancel.label} tooltip={this.props.context.locale.entitytype.deletedialog.buttons.cancel.tooltip} width={60} leftgap={8} onTouch={this._cancelDelete} />
                    </div>
                </Dialog>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Screens/Entitytypes/Entitytypes.js